package com.swetdot.swetgram.data.moderation

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.domain.DiscoverCommentId
import com.swetdot.swetgram.domain.DiscoverPostId
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.dto.AdminBlockUserRequest
import com.swetdot.swetgram.protocol.dto.ModerationLimits
import com.swetdot.swetgram.protocol.dto.ReportPage
import com.swetdot.swetgram.protocol.dto.ResolveReportRequest
import com.swetdot.swetgram.protocol.dto.ReportAction as ReportActionDto
import com.swetdot.swetgram.protocol.dto.ReportDecision as ReportDecisionDto
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import com.swetdot.swetgram.protocol.dto.Report as ReportDto

/**
 * The eight operations frozen v1.0.0 tags `Admin`, and nothing else.
 *
 * | operation                     | verb + path                                     | here            |
 * |-------------------------------|-------------------------------------------------|-----------------|
 * | `adminListReports`            | `GET    /admin/reports`                         | [reports]       |
 * | `adminGetReport`              | `GET    /admin/reports/{report_id}`             | [report]        |
 * | `adminResolveReport`          | `POST   /admin/reports/{report_id}/resolve`     | [resolve]       |
 * | `adminBlockUser`              | `POST   /admin/users/{user_id}/block`           | [suspendUser]   |
 * | `adminUnblockUser`            | `DELETE /admin/users/{user_id}/block`           | [liftSuspension]|
 * | `adminDeleteDiscoverPost`     | `DELETE /admin/discover/posts/{post_id}`        | [deletePost]    |
 * | `adminDeleteDiscoverComment`  | `DELETE /admin/discover/comments/{comment_id}`  | [deleteComment] |
 * | `adminDeleteMessage`          | `DELETE /admin/messages/{message_id}`           | [deleteMessage] |
 *
 * All eight are implemented; **four have no screen** and that is stated rather than left to be
 * discovered — see "What has no surface" below. The precedent is `GroupRepository`, which
 * implements twelve operations and gives three of them no screen for the same reason: the data
 * layer mirrors the contract, and the UI is where scope lives.
 *
 *
 * EVERY ONE OF THESE ANSWERS 403 FOR AN ORDINARY ACCOUNT
 *
 * `admin_role_required`, produced against `users.role`, which is `user` for every account in the
 * database until somebody runs `UPDATE users SET role = 'admin' WHERE username = '…';` by hand.
 * There is no operation in v1.0.0 that sets a role, deliberately — an endpoint that granted admin
 * would be a privilege-escalation surface guarded only by itself. So on a fresh deployment all
 * eight of these answer 403 for everybody, which is the correct behaviour and not a bug to chase.
 *
 * Moderator and admin are EQUAL across all eight (SPS §16.4 grants "admin/moderator" one list, and
 * the contract names nobody behind its 403s). The client's own gate — `AccountRole` from `getMe` —
 * therefore treats them as one audience too. That gate is a CONVENIENCE, not a boundary: it decides
 * what to draw, and the server decides what may happen. A client-side role check is one recompile
 * away from being false.
 *
 *
 * WHAT HAS NO SURFACE, AND WHY
 *
 *  * [deletePost], [deleteComment], [deleteMessage] — reachable through [resolve] with
 *    [ReportAction.DELETE_CONTENT], which deletes AND decides the report in one server-side
 *    transaction. A standalone delete would leave the report open and the deletion unattributed to
 *    any decision, which is precisely what `moderation_actions` exists to prevent.
 *  * [liftSuspension] — there is no operation in v1.0.0 that LISTS suspensions and none that
 *    reports whether a given account is suspended (`UserPublic` carries no such field). The only
 *    way to reach this one is to already know the account's uuid, and a uuid entry box on a phone
 *    is not a feature. Known gap; recorded in CHANGELOG.md.
 *
 *
 * NO CACHE, NO PAGE MERGING, NO OPTIMISM. A moderation queue read from a stale local copy would
 * show a report another moderator has already decided, and acting on it is how the same account
 * gets suspended twice for one thing. Every read here goes to the server.
 */
class AdminRepository(
    private val api: ApiClient,
) {

    /**
     * `adminListReports`. 200, cursor-paginated, newest first.
     *
     * Both filters are OPTIONAL and both are the contract's own enums; Ktor drops a null parameter,
     * so a null here sends no filter at all rather than an empty one. The server validates an
     * unrecognised value with a 400 instead of silently returning the whole table, which is why
     * these are typed rather than strings.
     *
     * [cursor] is opaque and server-owned: never parsed, never compared, never persisted across a
     * sign-out, and reset on every refresh because a cursor from the previous page set points into
     * a list that no longer exists.
     */
    suspend fun reports(
        status: ReportStatus? = null,
        targetKind: ReportTargetKind? = null,
        cursor: String? = null,
        limit: Int = ModerationLimits.PAGE_LIMIT_DEFAULT,
    ): Page<ModerationReport> {
        val page: ReportPage = api.get("admin/reports") {
            // `UNKNOWN` is a decode-side member and is not a value the contract's query enum
            // accepts; sending it would be a guaranteed 400 for a filter nobody chose.
            parameter("status", status?.takeIf { it != ReportStatus.UNKNOWN }?.wireValue())
            parameter("target_type", targetKind?.wireValue)
            parameter("cursor", cursor)
            // The contract bounds this at 1..100. Clamping turns a caller's bad number into the
            // nearest legal page instead of a 400 over a queue that is already on screen.
            parameter(
                "limit",
                limit.coerceIn(ModerationLimits.PAGE_LIMIT_MIN, ModerationLimits.PAGE_LIMIT_MAX),
            )
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `adminGetReport`. 200, or 404 `report_not_found`.
     *
     * NO SIDE EFFECTS — it does not claim the report into `reviewing`, because it is a GET. That is
     * the server's rule and it is why `reviewing` is a status nothing writes.
     *
     * Worth the extra round trip even though [reports] already returned the whole row: the list is
     * a snapshot, and re-reading before the resolve form is drawn is what stops a moderator
     * deciding a report a colleague decided while the queue sat on screen. The re-read cannot
     * PREVENT that — only the server's own already-decided rule can, and it answers 200 with the
     * decision that stands — but it means the screen shows the truth before anything is pressed.
     */
    suspend fun report(reportId: String): ModerationReport {
        val response: ReportDto = api.get("admin/reports/$reportId")
        return response.toDomain()
    }

    /**
     * `adminResolveReport`. 200 with the report as it now stands.
     *
     * [action] runs BEFORE the status write and in the same transaction, so a side effect that
     * fails leaves the report undecided rather than decided-and-not-acted-on.
     *
     * ALREADY-DECIDED REPORTS ARE NOT AN ERROR. The server answers 200 with the decision that
     * stands and performs NOTHING — it does not re-delete or re-suspend. So a second resolve is
     * safe, and the caller must read the returned status rather than assume its own request won.
     *
     * [note] is stored in `reports.resolution_note` and **no v1.0.0 operation can read it back**:
     * `Report` has no property for it. It is not discarded — the server writes it — but nothing in
     * this app will ever show it again, and the UI that collects it says so.
     */
    suspend fun resolve(
        reportId: String,
        decision: ReportDecision,
        action: ReportAction = ReportAction.NONE,
        note: String? = null,
    ): ModerationReport {
        val response: ReportDto = api.post("admin/reports/$reportId/resolve") {
            setBody(
                ResolveReportRequest(
                    decision = decision.toDto(),
                    action = action.toDto(),
                    note = note,
                ),
            )
        }
        return response.toDomain()
    }

    /**
     * `adminBlockUser`. 204.
     *
     * A SUSPENSION, not a block. It writes `user_suspensions` and revokes every live session the
     * account has (taking those sessions' push tokens with them); it is nothing like the personal,
     * symmetric `user_blocks` row that `BlockRepository` writes, and it does not appear in anybody's
     * blocked list.
     *
     * [reason] is REQUIRED by the contract, capped at 500, and that is right: a suspension nobody
     * can review — including the moderator who imposed it — is one that cannot be lifted with any
     * confidence.
     *
     * REFUSED AGAINST STAFF. Suspending a moderator or an admin answers `400 validation_failed` on
     * `user_id`, whoever asks, so that one compromised moderator account cannot lock out every
     * account that could undo it. A useful property falls out of that: nobody can suspend
     * themselves, because every caller who gets this far is staff.
     *
     * IT ACTUALLY BITES, and this comment used to say it did not. Revoking the sessions is only the
     * immediate half; the server also refuses the account a *new* session at sign-in
     * (`AuthService.kt:135`, after the password check, so the endpoint does not become an oracle)
     * and refuses every authenticated request made with an access token issued before the
     * suspension (the JWT `validate` block at `Module.kt:270`). That second half is the one that
     * matters to a client: an access token is stateless, so revoking sessions does not invalidate
     * one already in a device's memory, and a suspension used to begin up to one token lifetime
     * late and then end at the next sign-in.
     *
     * What that means HERE: after this returns, the suspended account's in-flight requests start
     * failing on their own. A caller does not need to do anything else, and must not assume the
     * account can quietly carry on.
     */
    suspend fun suspendUser(userId: UserId, reason: String) {
        api.post<Unit>("admin/users/${userId.value}/block") {
            setBody(AdminBlockUserRequest(reason = reason.take(ModerationText.BLOCK_REASON_MAX)))
        }
    }

    /**
     * `adminUnblockUser`. 204.
     *
     * Stamps `lifted_at` and KEEPS the suspension row, so "has this account been suspended before,
     * and what for" stays answerable — the opposite of a group ban, which is deleted because it is
     * consulted on every admission.
     *
     * It does NOT restore the sessions [suspendUser] revoked. The account can sign in again; it
     * cannot resume where it was.
     */
    suspend fun liftSuspension(userId: UserId) {
        api.delete<Unit>("admin/users/${userId.value}/block")
    }

    /**
     * `adminDeleteDiscoverPost`. 204.
     *
     * A HARD delete that cascades to every comment on the post, including other people's, plus its
     * likes and mentions. There is no undelete anywhere in v1.0.0. See the class note for why this
     * has no screen.
     */
    suspend fun deletePost(postId: DiscoverPostId) {
        api.delete<Unit>("admin/discover/posts/${postId.value}")
    }

    /** `adminDeleteDiscoverComment`. 204, hard, and it decrements the post's comment count. */
    suspend fun deleteComment(commentId: DiscoverCommentId) {
        api.delete<Unit>("admin/discover/comments/${commentId.value}")
    }

    /**
     * `adminDeleteMessage`. 204.
     *
     * A SOFT delete, unlike the two above, and the difference is the reconciliation feed: `messages`
     * has one and Discover does not, so a message leaves a tombstone every client can sync to. It
     * bypasses both the author check and the 15-minute window an ordinary delete obeys. Still
     * irreversible.
     */
    suspend fun deleteMessage(messageId: MessageId) {
        api.delete<Unit>("admin/messages/${messageId.value}")
    }
}

// ---------------------------------------------------------------------------------------------
// Boundary
// ---------------------------------------------------------------------------------------------

/**
 * The query filter's spelling. Not a property on [ReportStatus] itself, because `UNKNOWN` has no
 * legal wire value here — it is a decode-side member — and a property would have to invent one.
 */
private fun ReportStatus.wireValue(): String? = when (this) {
    ReportStatus.OPEN -> "open"
    ReportStatus.REVIEWING -> "reviewing"
    ReportStatus.RESOLVED -> "resolved"
    ReportStatus.REJECTED -> "rejected"
    ReportStatus.UNKNOWN -> null
}

private fun ReportDecision.toDto(): ReportDecisionDto = when (this) {
    ReportDecision.RESOLVE -> ReportDecisionDto.RESOLVE
    ReportDecision.REJECT -> ReportDecisionDto.REJECT
}

private fun ReportAction.toDto(): ReportActionDto = when (this) {
    ReportAction.NONE -> ReportActionDto.NONE
    ReportAction.DELETE_CONTENT -> ReportActionDto.DELETE_CONTENT
    ReportAction.BLOCK_USER -> ReportActionDto.BLOCK_USER
}
